import { X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import Button from '../button';

type ModalAction = {
	label: string;
	onClick: () => void;
};

export type ModalProps = {
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	visual?: ReactNode;
	children?: ReactNode;
	primaryAction?: ModalAction;
	secondaryAction?: ModalAction;
};

/**
 * 공통 모달 컴포넌트
 *
 * `isOpen`으로 노출 여부를 제어하고, `onClose`로 배경/닫기 버튼 클릭 시 동작을 전달합니다.
 * `title`, `children`, `visual`, `primaryAction`, `secondaryAction`은 필요한 조합만 전달합니다.
 *
 * @example
 * ```tsx
 * <Modal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="정말로 삭제하시겠습니까?"
 *   primaryAction={{ label: '확인', onClick: handleConfirm }}
 *   secondaryAction={{ label: '취소', onClick: () => setIsOpen(false) }}
 * >
 *   삭제한 데이터는 복구할 수 없습니다.
 * </Modal>
 * ```
 */
const Modal = ({
	isOpen,
	onClose,
	title,
	visual,
	children,
	primaryAction,
	secondaryAction,
}: ModalProps) => {
	const hasActions = primaryAction || secondaryAction;
	const hasContent = visual || children;

	return createPortal(
		<AnimatePresence>
			{isOpen && (
				<>
					{/* Overlay */}
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						className="fixed inset-0 z-50 bg-black/40"
						onClick={onClose}
					/>

					{/* Modal Content */}
					<div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center px-5">
						<motion.div
							initial={{ opacity: 0, scale: 0.95, y: 20 }}
							animate={{ opacity: 1, scale: 1, y: 0 }}
							exit={{ opacity: 0, scale: 0.95, y: 20 }}
							transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
							className="pointer-events-auto w-full max-w-[335px] rounded-lg bg-white shadow-xl"
							onClick={(e) => e.stopPropagation()}
						>
							{/* Header */}
							<div className="flex items-center justify-between px-5 pt-5 pb-4">
								{title && (
									<h2 className="font-medium text-mantang-gray-900 text-xl">
										{title}
									</h2>
								)}
								<button
									type="button"
									onClick={onClose}
									className="-mr-2 ml-auto cursor-pointer rounded-full p-2 transition-colors hover:bg-mantang-gray-100"
									aria-label="닫기"
								>
									<X className="size-5 text-mantang-gray-500" />
								</button>
							</div>

							{/* Content */}
							{hasContent && (
								<div className="flex flex-col items-center gap-4 px-5 pb-5">
									{visual && <div className="w-full">{visual}</div>}
									{children && (
										<div className="w-full text-base text-mantang-gray-700 leading-[22px]">
											{children}
										</div>
									)}
								</div>
							)}

							{/* Actions */}
							{hasActions && (
								<div className="flex gap-2 px-5 pb-5">
									{secondaryAction && (
										<Button
											variant="secondary"
											onClick={secondaryAction.onClick}
											className="max-w-none flex-1 rounded-lg"
										>
											{secondaryAction.label}
										</Button>
									)}
									{primaryAction && (
										<Button
											onClick={primaryAction.onClick}
											className="max-w-none flex-1 rounded-lg"
										>
											{primaryAction.label}
										</Button>
									)}
								</div>
							)}
						</motion.div>
					</div>
				</>
			)}
		</AnimatePresence>,
		document.body,
	);
};

export default Modal;
